import { FunctionComponent } from 'react'
import { BackdropIcon, GifIcon, IconProps, StickerIcon, VideoIcon } from '../components/icons'
import {
    MAX_OUTPUT_GIF_DURATION,
    MAX_OUTPUT_STICKER_DURATION,
    MAX_OUTPUT_VIDEO_DURATION,
    MIN_OUTPUT_VIDEO_DURATION,
} from '../constants'
import CreationToolGradient from './gradient'
import CreationToolUserRestriction from './user-restriction'

/**
 * The creation tool's type of output file.
 */
enum CreationToolOutputType {
    Backdrop = 'CT_OUTPUT_TYPE_BACKDROP',
    Gif = 'CT_OUTPUT_TYPE_GIF',
    Sticker = 'CT_OUTPUT_TYPE_STICKER',
    Video = 'CT_OUTPUT_TYPE_VIDEO',
}

namespace CreationToolOutputType {
    /**
     * Returns all of the output types.
     */
    export function all(): CreationToolOutputType[] {
        return [CreationToolOutputType.Gif, CreationToolOutputType.Sticker, CreationToolOutputType.Video]
    }

    /**
     * Returns the output type's display title.
     */
    export function title(type: CreationToolOutputType): string {
        switch (type) {
            case CreationToolOutputType.Backdrop:
                return 'Backdrop'
            case CreationToolOutputType.Gif:
                return 'GIF'
            case CreationToolOutputType.Sticker:
                return 'Sticker'
            case CreationToolOutputType.Video:
                return 'Clip'
        }
    }

    /**
     * Returns the output type's max duration (in seconds)
     */
    export function maxDuration(type: CreationToolOutputType): number {
        switch (type) {
            case CreationToolOutputType.Backdrop:
            case CreationToolOutputType.Video:
                return MAX_OUTPUT_VIDEO_DURATION
            case CreationToolOutputType.Gif:
                return MAX_OUTPUT_GIF_DURATION
            case CreationToolOutputType.Sticker:
                return MAX_OUTPUT_STICKER_DURATION
        }
    }

    /**
     * Returns the output type's min duration (in seconds)
     */
    export function minDuration(type: CreationToolOutputType): number {
        switch (type) {
            case CreationToolOutputType.Video:
                return MIN_OUTPUT_VIDEO_DURATION
            default:
                return 0.1
        }
    }

    /**
     * Returns whether or not the output type requires audio.
     */
    export function requiresAudio(type: CreationToolOutputType): boolean {
        switch (type) {
            case CreationToolOutputType.Video:
                return true
            default:
                return false
        }
    }

    /**
     * Returns whether or not the output type requires transparent pixels.
     */
    export function requiresTransparency(type: CreationToolOutputType): boolean {
        switch (type) {
            case CreationToolOutputType.Sticker:
                return true
            default:
                return false
        }
    }

    /**
     * Returns whether or not the output type requires a title.
     */
    export function requiresTitle(type: CreationToolOutputType): boolean {
        switch (type) {
            case CreationToolOutputType.Video:
                return true
            default:
                return false
        }
    }

    /**
     * Returns user restrictions for the output type, if one was set.
     */
    export function userRestriction(type: CreationToolOutputType): CreationToolUserRestriction {
        switch (type) {
            case CreationToolOutputType.Video:
                return CreationToolUserRestriction.CanUploadVideo
            default:
                return CreationToolUserRestriction.Everyone
        }
    }

    /**
     * Returns the output type's uploaded base path.
     * This is used for redirects after successful uploads.
     */
    export function uploadedBasePath(type: CreationToolOutputType): string {
        switch (type) {
            case CreationToolOutputType.Sticker:
                return `/stickers`
            case CreationToolOutputType.Video:
                return `/clips`
            default:
                return `/gifs`
        }
    }

    /**
     * Returns the output type's upload creation_location value suffix.
     */
    export function creationLocationSuffix(type: CreationToolOutputType): string {
        switch (type) {
            case CreationToolOutputType.Sticker:
                return 'sticker'
            case CreationToolOutputType.Backdrop:
                return 'gif'
            case CreationToolOutputType.Gif:
                return 'gif'
            case CreationToolOutputType.Video:
                return 'video'
        }
    }

    /**
     * Returns the output type's icon react component.
     */
    export function icon(type: CreationToolOutputType): FunctionComponent<IconProps> {
        switch (type) {
            case CreationToolOutputType.Sticker:
                return StickerIcon
            case CreationToolOutputType.Gif:
                return GifIcon
            case CreationToolOutputType.Backdrop:
                return BackdropIcon
            case CreationToolOutputType.Video:
                return VideoIcon
        }
    }

    /**
     * Returns the output type's gradient colors.
     */
    export function gradient(type: CreationToolOutputType): CreationToolGradient {
        switch (type) {
            case CreationToolOutputType.Sticker:
                return CreationToolGradient.BlueGreen
            default:
                return CreationToolGradient.IndigoPurple
        }
    }

    /**
     * Returns whether or not the output type can be edited.
     */
    export function editable(type: CreationToolOutputType): boolean {
        switch (type) {
            case CreationToolOutputType.Video:
            case CreationToolOutputType.Gif:
            case CreationToolOutputType.Sticker:
                return true
            default:
                return false
        }
    }

    /**
     * Returns the output type's GA label
     */
    export function gaLabel(type: CreationToolOutputType): string {
        switch (type) {
            case CreationToolOutputType.Sticker:
                return 'sticker'
            case CreationToolOutputType.Backdrop:
            case CreationToolOutputType.Gif:
                return 'gif'
            case CreationToolOutputType.Video:
                return 'clip'
        }
    }
}

export default CreationToolOutputType
